import { IAppContext } from 'App/AppContext';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import { PermissionTypes } from 'models/auth/rolePermission';
import Control from 'models/control';
import Theme from 'models/theme';
import { SubscriptionTypes } from 'utils/subscription';
import { appRoles, appRoleSets, appRoleSetTypes } from './appRoles';

export enum FeatureTypes {
  OrgUnits = 1,
  Objectives = 2,
  KBTeams = 3,
  Automation = 4, //power automate flows, azure defender integration
  Dashboard = 5,
  DeleteStandardElement = 6,
  TaskChangeCompletionDate = 7,
  AIAssistant = 8,
  Assets = 10,
  SupplyChain = 11,
  Events = 12, //creation and usage of event templates
  KPIsAndForms = 13, //management of KPIs and Forms
  Approvals = 14,
  TaskForm = 15, //show form at task level
  Organization = 16,
  Requirements = 17,
  Risks = 18,
  Controls = 19,
  AnnualPlan = 20,
  Tasks = 21,
  Library = 22,
  Standards = 23,
  Audit = 24,
  PowerBI = 25,
  KPIAlerts = 26,
  RBAC = 27,
  Processes = 28,
  Planner = 29,
  PlannerGroups = 30,
  ProcessingActivities = 31,
  ProcessingActivitiesDpia = 32,
  ReadConfirmations = 33,
  UserGroups = 34,
  Risk3dMatrix = 35,
  ProcessingActivityApprovals = 36,
  AuditParticipant = 37,
  Projects = 39, //the Projects module: project list + project details scene
  Admin = 50,
  AdminAuth = 51,
  AdminSubscription = 52,
  AdminOrgUnits = 53,
  AdminPermissions = 54,
  PartnerAdmin = 60,
  TenantAdmin = 300,
  TenantGlobalAdmin = 301,
  TenantISOAdmin = 302,
  TenantDeleteISOStandardElement = 303,
  GenericManager = 500,
  PreviewLanguages = 1001,
  PreviewFeatures = 1002,
}

// Mapping to determine which features are available for subscription types
// Empty array means available for all
const subscriptionTypeFeatureMapping: Record<Partial<FeatureTypes>, SubscriptionTypes[]> = {
  [FeatureTypes.OrgUnits]: [SubscriptionTypes.Premium],
  [FeatureTypes.KBTeams]: [SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.DeleteStandardElement]: [],
  [FeatureTypes.PreviewLanguages]: [SubscriptionTypes.Internal],
  [FeatureTypes.PreviewFeatures]: [SubscriptionTypes.Internal],
  [FeatureTypes.Dashboard]: [],
  [FeatureTypes.AdminAuth]: [],
  [FeatureTypes.AdminPermissions]: [],
  [FeatureTypes.AdminSubscription]: [],
  [FeatureTypes.AdminOrgUnits]: [SubscriptionTypes.Premium],
  [FeatureTypes.TenantAdmin]: [],
  [FeatureTypes.TenantGlobalAdmin]: [],
  [FeatureTypes.TenantISOAdmin]: [],
  [FeatureTypes.GenericManager]: [],
  [FeatureTypes.TaskChangeCompletionDate]: [],
  [FeatureTypes.TenantDeleteISOStandardElement]: [],
  [FeatureTypes.Assets]: [],
  [FeatureTypes.SupplyChain]: [SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.PartnerAdmin]: [],
  [FeatureTypes.Processes]: [SubscriptionTypes.Basic, SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.Objectives]: [SubscriptionTypes.Basic, SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.Automation]: [SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.Events]: [SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.KPIsAndForms]: [SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.TaskForm]: [SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.Approvals]: [SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.Organization]: [SubscriptionTypes.Basic, SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.Requirements]: [SubscriptionTypes.Basic, SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.Risks]: [],
  [FeatureTypes.Controls]: [],
  [FeatureTypes.AnnualPlan]: [],
  [FeatureTypes.Tasks]: [],
  [FeatureTypes.Library]: [],
  [FeatureTypes.Standards]: [
    SubscriptionTypes.NIS2,
    SubscriptionTypes.Basic,
    SubscriptionTypes.Business,
    SubscriptionTypes.Premium,
  ],
  [FeatureTypes.Admin]: [],
  [FeatureTypes.Audit]: [SubscriptionTypes.Premium],
  [FeatureTypes.AuditParticipant]: [SubscriptionTypes.Premium],
  [FeatureTypes.PowerBI]: [SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.RBAC]: [SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.KPIAlerts]: [SubscriptionTypes.Premium],
  [FeatureTypes.Planner]: [],
  [FeatureTypes.PlannerGroups]: [SubscriptionTypes.Business, SubscriptionTypes.Premium],
  //every paid plan has the assistant (the API has no mapping entry for it); Portal is refused by the early return of
  //hasSubscriptionFeature
  [FeatureTypes.AIAssistant]: [
    SubscriptionTypes.Basic,
    SubscriptionTypes.Business,
    SubscriptionTypes.Premium,
    SubscriptionTypes.NIS2,
  ],
  [FeatureTypes.ProcessingActivities]: [],
  [FeatureTypes.ProcessingActivitiesDpia]: [SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.ReadConfirmations]: [SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.UserGroups]: [SubscriptionTypes.Business, SubscriptionTypes.Premium],
  [FeatureTypes.Risk3dMatrix]: [SubscriptionTypes.Premium],
  [FeatureTypes.ProcessingActivityApprovals]: [SubscriptionTypes.Premium, SubscriptionTypes.Business],
  //available to every licensed tenant - exposure is controlled by release, not by this flag
  [FeatureTypes.Projects]: [],
};

// Mapping of which features are available for each role
// Empty array means available for all
const roleFeatureMapping: Record<FeatureTypes, appRoles[]> = {
  [FeatureTypes.OrgUnits]: [],
  [FeatureTypes.Objectives]: [],
  [FeatureTypes.Processes]: [],
  [FeatureTypes.KBTeams]: appRoleSets[appRoleSetTypes.Managers],
  [FeatureTypes.DeleteStandardElement]: appRoleSets[appRoleSetTypes.Admins],
  [FeatureTypes.PreviewLanguages]: appRoleSets[appRoleSetTypes.Managers],
  [FeatureTypes.PreviewFeatures]: [],
  [FeatureTypes.Dashboard]: appRoleSets[appRoleSetTypes.Users],
  [FeatureTypes.AdminAuth]: [...appRoleSets[appRoleSetTypes.Admins], appRoles.LicenseManager],
  [FeatureTypes.AdminPermissions]: [...appRoleSets[appRoleSetTypes.Admins]],
  [FeatureTypes.AdminSubscription]: [...appRoleSets[appRoleSetTypes.Admins], appRoles.LicenseManager],
  [FeatureTypes.AdminOrgUnits]: appRoleSets[appRoleSetTypes.Admins],
  [FeatureTypes.TenantAdmin]: appRoleSets[appRoleSetTypes.TenantAdmins],
  [FeatureTypes.TenantGlobalAdmin]: [appRoles.TenantGlobalAdmin],
  [FeatureTypes.TenantISOAdmin]: [appRoles.TenantISOAdmin, appRoles.TenantGlobalAdmin],
  [FeatureTypes.GenericManager]: appRoleSets[appRoleSetTypes.Managers],
  [FeatureTypes.TaskChangeCompletionDate]: appRoleSets[appRoleSetTypes.Admins],
  [FeatureTypes.TenantDeleteISOStandardElement]: [appRoles.TenantGlobalAdmin],
  [FeatureTypes.Assets]: [],
  [FeatureTypes.SupplyChain]: [],
  //Admin, OrgAdmin and Manager only. Consultants are licensed but must not reach a partner's customers, so this
  //deliberately excludes them and mirrors Roles.PartnerCustomerAccessList on the back-end exactly.
  [FeatureTypes.PartnerAdmin]: appRoleSets[appRoleSetTypes.Licensed],
  [FeatureTypes.Automation]: appRoleSets[appRoleSetTypes.Managers],
  [FeatureTypes.KPIsAndForms]: appRoleSets[appRoleSetTypes.Managers],
  [FeatureTypes.TaskForm]: [],
  [FeatureTypes.Events]: appRoleSets[appRoleSetTypes.Managers],
  [FeatureTypes.Approvals]: appRoleSets[appRoleSetTypes.Managers],
  [FeatureTypes.Organization]: [],
  [FeatureTypes.Requirements]: [],
  [FeatureTypes.Risks]: [],
  [FeatureTypes.Controls]: [],
  [FeatureTypes.AnnualPlan]: [],
  [FeatureTypes.Tasks]: [],
  [FeatureTypes.Library]: [],
  [FeatureTypes.Standards]: [],
  [FeatureTypes.Admin]: appRoleSets[appRoleSetTypes.Managers],
  // An audit exposes the findings and conclusions about the whole organization, so MANAGING audits
  // is for managers: the list, the annual plan, the details and reporting tabs, every write.
  [FeatureTypes.Audit]: appRoleSets[appRoleSetTypes.Managers],
  // A participant named on an audit's team reaches that one audit's execute tab, read-only, because
  // the invitation mail asks them to start work there. Read access only: edit rights still come from
  // the RBAC data permission alone, so being on the team grants nothing on its own.
  [FeatureTypes.AuditParticipant]: appRoleSets[appRoleSetTypes.Users],
  [FeatureTypes.PowerBI]: appRoleSets[appRoleSetTypes.Managers],
  [FeatureTypes.RBAC]: [],
  [FeatureTypes.KPIAlerts]: appRoleSets[appRoleSetTypes.Managers],
  [FeatureTypes.Planner]: [],
  [FeatureTypes.PlannerGroups]: [],
  [FeatureTypes.AIAssistant]: appRoleSets[appRoleSetTypes.Managers],
  [FeatureTypes.ProcessingActivities]: [],
  [FeatureTypes.ProcessingActivitiesDpia]: [],
  [FeatureTypes.ReadConfirmations]: [],
  [FeatureTypes.UserGroups]: [],
  [FeatureTypes.Risk3dMatrix]: [],
  [FeatureTypes.ProcessingActivityApprovals]: [],
  [FeatureTypes.Projects]: appRoleSets[appRoleSetTypes.Users],
};

// Mapping to determine which features are available for subscription types
// Empty array means available for all
const permissionFeatureMapping: Record<Partial<FeatureTypes>, PermissionTypes[]> = {
  [FeatureTypes.OrgUnits]: [],
  [FeatureTypes.KBTeams]: [PermissionTypes.ModuleLibrary],
  [FeatureTypes.DeleteStandardElement]: [],
  [FeatureTypes.PreviewLanguages]: [],
  [FeatureTypes.PreviewFeatures]: [],
  [FeatureTypes.Dashboard]: [],
  [FeatureTypes.AdminAuth]: [],
  [FeatureTypes.AdminPermissions]: [],
  [FeatureTypes.AdminSubscription]: [],
  [FeatureTypes.AdminOrgUnits]: [],
  [FeatureTypes.TenantAdmin]: [],
  [FeatureTypes.TenantGlobalAdmin]: [],
  [FeatureTypes.TenantISOAdmin]: [],
  [FeatureTypes.GenericManager]: [],
  [FeatureTypes.TaskChangeCompletionDate]: [],
  [FeatureTypes.TenantDeleteISOStandardElement]: [],
  [FeatureTypes.Assets]: [PermissionTypes.ModuleAssets],
  [FeatureTypes.SupplyChain]: [PermissionTypes.ModuleAssets],
  [FeatureTypes.PartnerAdmin]: [PermissionTypes.ModulePartner],
  [FeatureTypes.Processes]: [PermissionTypes.ModuleOrg],
  [FeatureTypes.Objectives]: [PermissionTypes.ModuleOrg],
  [FeatureTypes.Automation]: [],
  [FeatureTypes.Events]: [],
  [FeatureTypes.KPIsAndForms]: [PermissionTypes.ModuleOrg],
  [FeatureTypes.TaskForm]: [],
  [FeatureTypes.Approvals]: [],
  [FeatureTypes.Organization]: [PermissionTypes.ModuleOrg],
  [FeatureTypes.Requirements]: [PermissionTypes.ModuleRequirements],
  [FeatureTypes.Risks]: [PermissionTypes.ModuleRisks],
  [FeatureTypes.Controls]: [PermissionTypes.ModuleControls],
  [FeatureTypes.AnnualPlan]: [PermissionTypes.ModuleAnnualPlan],
  [FeatureTypes.Tasks]: [PermissionTypes.ModuleTasks],
  [FeatureTypes.Library]: [PermissionTypes.ModuleLibrary],
  [FeatureTypes.Standards]: [PermissionTypes.ModuleStandards],
  [FeatureTypes.Admin]: [PermissionTypes.ModuleAdmin],
  [FeatureTypes.Audit]: [],
  [FeatureTypes.AuditParticipant]: [],
  [FeatureTypes.PowerBI]: [],
  [FeatureTypes.RBAC]: [],
  [FeatureTypes.KPIAlerts]: [],
  [FeatureTypes.Planner]: [],
  [FeatureTypes.PlannerGroups]: [],
  [FeatureTypes.AIAssistant]: [],
  [FeatureTypes.ProcessingActivities]: [PermissionTypes.ModuleProcessingActivities],
  [FeatureTypes.ProcessingActivitiesDpia]: [PermissionTypes.ModuleProcessingActivities],
  [FeatureTypes.ReadConfirmations]: [],
  [FeatureTypes.UserGroups]: [],
  [FeatureTypes.Risk3dMatrix]: [],
  [FeatureTypes.ProcessingActivityApprovals]: [PermissionTypes.CreateProcessingActivity],
  //Projects is assigned per user, like every other module: a person reaches the project when one of
  //their roles carries ModuleProjects. It used to require no permission at all - the project was
  //treated as a reader surface whose writes the route policy alone guarded - which meant an
  //administrator had no way to give it to some people and not others.
  //
  //The server agrees: ProjectModuleAccess refuses every Project WRITE without this permission
  //(IP0001). Reads stay open there; hiding the menu item is this map's job.
  [FeatureTypes.Projects]: [PermissionTypes.ModuleProjects],
};

export const hasSubscriptionFeature = (type: SubscriptionTypes, feature: FeatureTypes): boolean => {
  if (type === SubscriptionTypes.Internal) return true;
  if (type === SubscriptionTypes.Container) return false;
  if (type === SubscriptionTypes.Portal) {
    return feature === FeatureTypes.AdminAuth || feature === FeatureTypes.PartnerAdmin;
  }

  const supportedTypes = subscriptionTypeFeatureMapping[feature];
  if (supportedTypes.length === 0 || supportedTypes.indexOf(type) >= 0) {
    return true;
  }

  return false;
};

export const hasRoleFeature = (roles: number[] | undefined, feature: FeatureTypes): boolean => {
  const requiredRoles = roleFeatureMapping[feature];
  if (requiredRoles.length === 0) return true;

  if (!roles || roles.length === 0) {
    if (requiredRoles.includes(appRoles.User)) {
      return true;
    }
  } else {
    for (let idx = 0; idx < requiredRoles.length; idx++) {
      if (roles.includes(requiredRoles[idx])) {
        return true;
      }
    }
  }

  return false;
};

const hasPermissionFeature = (
  roles: number[] | undefined,
  permissions: number[] | undefined,
  feature: FeatureTypes,
): boolean => {
  //The admin role has all permissions
  //if (roles && roles.length > 0 && hasAdminRole(roles)) {
  //  return true;
  //}

  const requiredPermissions = permissionFeatureMapping[feature];
  if (requiredPermissions.length === 0) return true;

  //check permissions
  if (!permissions || permissions.length === 0) {
    return false;
  } else {
    for (let idx = 0; idx < requiredPermissions.length; idx++) {
      if (permissions.includes(requiredPermissions[idx])) {
        return true;
      }
    }
  }

  return false;
};

const hasAdminRole = (roles: number[] | undefined): boolean => {
  if (
    roles &&
    roles.length > 0 &&
    (roles.includes(appRoles.Admin) ||
      roles.includes(appRoles.OrgAdmin) ||
      roles.includes(appRoles.TenantGlobalAdmin) ||
      roles.includes(appRoles.TenantISOAdmin))
  ) {
    return true;
  }

  return false;
};

export const hasUserFeatureGenericManager = (appContext: IAppContext): boolean => {
  return hasRolesFeatureGenericManager(appContext.user.login.userSystemRoles);
};

//The same check from the roles alone, for callers that run before an app context exists: the sign-in
//bootstrap holds the login result but not yet a context to read it from.
export const hasRolesFeatureGenericManager = (roles: number[] | undefined): boolean => {
  return hasRoleFeature(roles, FeatureTypes.GenericManager);
};

/// <summary>
/// Logs a detailed breakdown of why a feature check passed or failed.
/// Only logs in development builds. Call this when debugging permission issues.
/// </summary>
export const debugFeatureAccess = (appContext: IAppContext, feature: FeatureTypes, label?: string): void => {
  if (process.env.NODE_ENV !== 'development') return;

  const login = appContext.user.login;
  const subType = login.subscriptionType;
  const roles = login.userSystemRoles;
  const permissions = login.permissions;

  const subOk = hasSubscriptionFeature(subType, feature);
  const roleOk = hasRoleFeature(roles, feature);
  const permOk = hasPermissionFeature(roles, permissions, feature);
  const overall = subOk && roleOk && permOk;

  const featureName = FeatureTypes[feature] ?? feature;
  const subName = SubscriptionTypes[subType] ?? subType;

  console.groupCollapsed(
    `%c[FeatureAccess] ${label ?? featureName}: ${overall ? 'GRANTED' : 'DENIED'}`,
    overall ? 'color: green; font-weight: bold' : 'color: red; font-weight: bold',
  );
  console.table({
    feature: { value: featureName, check: 'N/A' },
    subscription: { value: subName, check: subOk ? 'pass' : 'FAIL' },
    roles: { value: (roles ?? []).join(', ') || '(none)', check: roleOk ? 'pass' : 'FAIL' },
    permissions: { value: (permissions ?? []).join(', ') || '(none)', check: permOk ? 'pass' : 'FAIL' },
    userLicensed: { value: login.userLicensed, check: 'info' },
    tenantLicensed: { value: login.tenantLicensed, check: 'info' },
  });
  console.groupEnd();
};

export const hasUserFeature = (
  appContext: IAppContext,
  feature: FeatureTypes,
  permission?: PermissionTypes,
): boolean => {
  if (!hasSubscriptionFeature(appContext.user.login.subscriptionType, feature)) return false;
  if (!hasRoleFeature(appContext.user.login.userSystemRoles, feature)) return false;
  if (!hasPermissionFeature(appContext.user.login.userSystemRoles, appContext.user.login.permissions, feature))
    return false;
  if (permission && !appContext.user.login.permissions?.includes(permission)) return false;

  return true;
};

export const hasUserRolePermission = (appContext: IAppContext, permission: PermissionTypes): boolean => {
  if (denyPermissionsForSystemRoles(permission, appContext.user.login.userSystemRoles)) return false;

  return appContext.user.login.permissions?.includes(permission) ?? false;
};

const denyPermissionsForSystemRoles = (permission: PermissionTypes, systemRoles: number[] | undefined): boolean => {
  //
  // For normal users, all Role permissions are denied, except:
  // - module permissions
  // - CreateTask
  // - CreateLibraryItem
  // - CreateProcessingActivity
  //

  if (!systemRoles || systemRoles.length === 0) return true;

  switch (permission) {
    case PermissionTypes.ModuleAdmin:
    case PermissionTypes.ModuleLibrary:
    case PermissionTypes.ModuleOrg:
    case PermissionTypes.ModuleRequirements:
    case PermissionTypes.ModuleRisks:
    case PermissionTypes.ModuleControls:
    case PermissionTypes.ModuleAnnualPlan:
    case PermissionTypes.ModuleTasks:
    case PermissionTypes.ModuleStandards:
    case PermissionTypes.ModuleAssets:
    case PermissionTypes.ModulePartner:
    case PermissionTypes.ModuleProjects:
    case PermissionTypes.CreateTask:
    case PermissionTypes.CreateLibraryItem:
    case PermissionTypes.CreateProcessingActivity:
      return false;
    default:
      return !hasRoleFeature(systemRoles, FeatureTypes.GenericManager);
  }
};

export const convertAuthSchemaString = (schemas: string | undefined): (number | undefined)[] => {
  //From the back-end, for some APIs, we get a comma seperated string with authSchema Ids
  //1. In this string, '0' and 'undefined' must be translated to undefined.
  //2. When the string is empty, return 1 item with value undefined
  //3. Ignore any invalid authSchema Ids

  const authSchemas: (number | undefined)[] = [];

  if (schemas) {
    const items = schemas.split(',');
    items.forEach((item) => {
      const id = Number(item === 'undefined' ? 0 : item);
      if (!isNaN(id)) {
        authSchemas.push(id === 0 ? undefined : id);
      }
    });
  } else {
    authSchemas.push(undefined);
  }

  return authSchemas;
};

export const hasUserDataPermission = (
  appContext: IAppContext,
  authSchemaIds: (number | undefined)[],
  operation: AuthSchemaLineOperation,
) => {
  //when user has no other system role, user is always read-only
  if (!hasUserFeatureGenericManager(appContext)) return false;
  //when subscription is basic or nis2, user has all permissions
  if (
    appContext.user.login.subscriptionType === SubscriptionTypes.Basic ||
    appContext.user.login.subscriptionType === SubscriptionTypes.NIS2
  ) {
    return true;
  }
  //When there are no authSchemaIds, user has all permissions
  if (authSchemaIds.length === 0) return true;
  //When the user has an admin roles, data permissions do not apply
  if (hasAdminRole(appContext.user.login.userSystemRoles)) return true;

  //check the permissions
  const userId = appContext.user.id;
  if (authSchemaIds.length === 1) {
    if (authSchemaIds[0] === undefined) return true;

    return appContext.globalDataCache.authSchemas.get(authSchemaIds[0]).hasLine(userId, operation);
  } else {
    //when multiple schemas are provided, return true when the user has permissions on 1+ of them
    if (authSchemaIds.includes(undefined)) return true;

    //create list of unique schemas without undefined
    const unique = [...new Set(authSchemaIds)].filter((a) => a !== undefined) as number[];
    //get scchemas from cache
    const schemas = appContext.globalDataCache.authSchemas.getItemsForId(unique);

    return schemas.some((a) => a.hasLine(userId, operation));
  }
};

export const getStandardElementAuthSchemaIds = (
  items: (Control | Theme)[] | Control | Theme | undefined,
  appContext: IAppContext,
): (number | undefined)[] => {
  if (!items) return [];
  let elements: (Control | Theme)[];
  if (items instanceof Control || items instanceof Theme) {
    elements = [items];
  } else {
    elements = items;
  }

  const result: (number | undefined)[] = [];

  for (let idx = 0; idx < elements.length; idx++) {
    const element = elements[idx];
    const schemas = element.getAutSchemaIds(appContext);
    result.push(...schemas);
  }

  return result;
};
